import {
  useState,
  useEffect,
  ChangeEvent,
  ReactNode,
  useCallback,
  Fragment,
} from 'react';
import { Icon, Link } from '@nimbus-ds/components';
import { CheckCircleIcon, ChevronLeftIcon } from '@nimbus-ds/icons';
import { InteractiveList, SideModal } from '@nimbus-ds/patterns';
import uniq from 'lodash.uniq';
import { CancelAndConfirmButtons, Stack } from 'commons/components';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import GenericSelectionModalHeader from './GenericSelectionModalHeader';
import GenericSelectionModalList from './GenericSelectionModalList';
import { VISIBLE_OPTIONS_NUMBER } from '../constants/constants';
import ModalFooter from '../ModalFooter';

interface GenericSelectionModalProps<T> {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (selected: string[]) => void;
  preselected: string[];
  items: T[];
  getItemId: (item: T) => string;
  getItemSearchText: (item: T) => string;
  renderItem: (
    item: T,
    isSelected: boolean,
    onToggle: () => void,
    isDisabled: boolean,
  ) => ReactNode;
  translationPath: string;
  minItemsForSearch?: number;
  showSelectAll?: boolean;
  getItemDisabled?: (item: T) => boolean;
  getItemDisabledMessage?: (item: T) => string | undefined;
}

function GenericSelectionModal<T>({
  isOpen,
  onClose,
  onSubmit,
  preselected = [],
  items,
  getItemId,
  getItemSearchText,
  renderItem,
  translationPath,
  minItemsForSearch = VISIBLE_OPTIONS_NUMBER,
  showSelectAll = true,
  getItemDisabled,
  getItemDisabledMessage: _getItemDisabledMessage,
}: Readonly<GenericSelectionModalProps<T>>): JSX.Element {
  const tCommon = useTranslationShipping(
    'deliveryMethods.shippingLimiting.modals.common',
  );
  const t = useTranslationShipping(translationPath);

  const [newSelected, setNewSelected] = useState<string[]>(preselected);
  const [filteredItems, setFilteredItems] = useState<T[]>(items);
  const [selectAll, setSelectAll] = useState<boolean | 'indeterminate'>(false);
  const [searchTerm, setSearchTerm] = useState('');

  const getAllItemsChecked = useCallback(
    (selected: string[], scopeItems: T[]): boolean | 'indeterminate' => {
      const enabledItems = getItemDisabled
        ? scopeItems.filter((item) => !getItemDisabled(item))
        : scopeItems;

      if (selected.length === 0) return false;
      if (selected.length === enabledItems.length) return true;
      return 'indeterminate';
    },
    [getItemDisabled],
  );

  useEffect(() => {
    if (items.length > 0) {
      setFilteredItems(items);
      setSelectAll(getAllItemsChecked(preselected, items));
    }
  }, [getAllItemsChecked, items, preselected]);

  const handleToggle = (id: string, checked: boolean) => {
    const selectedItems = checked
      ? [...newSelected, id]
      : newSelected.filter((selected) => selected !== id);

    setNewSelected(selectedItems);
    setSelectAll(getAllItemsChecked(selectedItems, filteredItems));
  };

  const handleSelectAll = () => {
    if (!selectAll || selectAll === 'indeterminate') {
      const enabledItems = getItemDisabled
        ? filteredItems.filter((item) => !getItemDisabled(item))
        : filteredItems;
      const filteredIds = enabledItems.map(getItemId);
      setSelectAll(getAllItemsChecked(filteredIds, filteredItems));
      const selectedItems = uniq([...newSelected, ...filteredIds]);
      setNewSelected(selectedItems);
    } else {
      setSelectAll(false);
      setNewSelected([]);
    }
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    const filtered = items.filter((item) =>
      getItemSearchText(item)
        .toLowerCase()
        .includes(e.target.value.toLowerCase()),
    );
    setFilteredItems(filtered);
  };

  const handleSave = () => {
    onSubmit(newSelected);
  };

  const handleClearSelection = () => {
    setNewSelected([]);
    setSelectAll(false);
  };

  const counterText = t(newSelected.length === 1 ? 'counterOne' : 'counter', {
    count: newSelected.length,
  });

  return (
    <SideModal
      open={isOpen}
      onRemove={onClose}
      maxWidth={{ xs: '100%', md: '45%', lg: '540px' }}
      paddingBody="none"
      headerAction={
        <Link onClick={onClose} textDecoration="none">
          <Icon color="primary-textHigh" source={<ChevronLeftIcon />} />
        </Link>
      }
      headerIcon={
        <Link onClick={handleSave} textDecoration="none">
          <Icon source={<CheckCircleIcon />} />
        </Link>
      }
    >
      <Stack column align="stretch" height="calc(100vh - 85px)">
        <GenericSelectionModalHeader
          title={t('title')}
          description={t('description')}
          totalItems={items.length}
          selectedCount={newSelected.length}
          searchTerm={searchTerm}
          searchPlaceholder={t('searchPlaceholder')}
          counterText={counterText}
          minItemsForSearch={minItemsForSearch}
          onSearchChange={handleSearchChange}
          onClearSelection={handleClearSelection}
        />

        <GenericSelectionModalList
          items={filteredItems.map((item) => {
            const id = getItemId(item);
            const isDisabled = getItemDisabled ? getItemDisabled(item) : false;
            const node = renderItem(
              item,
              newSelected.includes(id),
              () => handleToggle(id, !newSelected.includes(id)),
              isDisabled,
            );
            return <Fragment key={id}>{node}</Fragment>;
          })}
          selectAllCheckbox={
            showSelectAll && filteredItems.length > 0 ? (
              <InteractiveList.CheckboxItem
                title={tCommon('selectAll')}
                checkbox={{
                  name: 'selectAll',
                  checked: selectAll === 'indeterminate' ? false : selectAll,
                  onChange: handleSelectAll,
                  indeterminate: selectAll === 'indeterminate',
                }}
              />
            ) : null
          }
          showEmptyMessage={filteredItems.length === 0 && !!searchTerm}
          emptyTitle={tCommon('emptySearchTitle')}
          emptyMessage={tCommon('emptySearchMessage')}
        />

        {filteredItems.length > 0 && (
          <ModalFooter>
            <CancelAndConfirmButtons
              confirmText={tCommon('save')}
              cancelText={tCommon('cancel')}
              onConfirm={handleSave}
              onCancel={onClose}
            />
          </ModalFooter>
        )}
      </Stack>
    </SideModal>
  );
}

export default GenericSelectionModal;
